import { useMemo, useState } from "react";
import SectionHeading from "./SectionHeading";
import { feedFilters, feedItems, type FeedItem, type TagType } from "@/mocks/siteData";

const tagStyles: Record<TagType, { chip: string; bar: string; dot: string }> = {
  后续: { chip: "bg-secondary-100 text-secondary-800", bar: "bg-secondary-400", dot: "text-secondary-600" },
  反转: { chip: "bg-primary-100 text-primary-700", bar: "bg-primary-500", dot: "text-primary-600" },
  回应: { chip: "bg-accent-100 text-accent-800", bar: "bg-accent-500", dot: "text-accent-600" },
  新进展: { chip: "bg-background-200 text-foreground-700", bar: "bg-foreground-700", dot: "text-foreground-700" },
};

export default function Feed() {
  const [active, setActive] = useState<"全部" | TagType>("全部");
  const [marked, setMarked] = useState<string[]>([]);

  const shown = useMemo(
    () => (active === "全部" ? feedItems : feedItems.filter((it) => it.tag === active)),
    [active]
  );

  const toggleMark = (id: string) => {
    setMarked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <section id="feed" className="scroll-mt-20 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <SectionHeading
            kicker="Latest Follow-ups"
            title="最新动态 · 进展不断更新"
            subtitle="把分散在评论区和各平台的后续、反转与回应按时间收进来，标记你关心的那条，回来继续蹲。"
          />
          <div className="inline-flex w-max items-center rounded-full border border-background-200 bg-background-100 p-1">
            {feedFilters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors md:px-4 ${
                  active === f
                    ? "bg-background-950 text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-bookmark-3-fill text-primary-500" />
          <span>
            已追更 <strong className="text-foreground-900">{marked.length}</strong> 条，可随时取消
          </span>
        </div>

        <ol className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {shown.map((item: FeedItem, idx: number) => {
            const style = tagStyles[item.tag];
            const isMarked = marked.includes(item.id);
            return (
              <li key={item.id}>
                <article className="group relative flex h-full flex-col gap-4 overflow-hidden rounded-xl border border-background-200 bg-background-100 p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-background-50 hover:shadow-lift">
                  <span className={`absolute inset-y-0 left-0 w-1 ${style.bar}`} aria-hidden="true" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex h-6 min-w-6 items-center justify-center rounded px-1.5 text-xs font-black ${style.dot}`}>
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-bold ${style.chip}`}>
                        {item.tag}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleMark(item.id)}
                      aria-pressed={isMarked}
                      aria-label={isMarked ? "取消追更" : "标记追更"}
                      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                        isMarked
                          ? "bg-primary-100 text-primary-700"
                          : "text-foreground-500 hover:bg-background-200 hover:text-foreground-800"
                      }`}
                    >
                      <i className={`${isMarked ? "ri-bookmark-3-fill" : "ri-bookmark-3-line"} text-sm`} />
                      {isMarked ? "已追" : "追更"}
                    </button>
                  </div>

                  <h3 className="font-heading text-[17px] font-bold leading-snug text-foreground-950 md:text-lg">
                    {item.title}
                  </h3>
                  <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">{item.summary}</p>

                  <div className="mt-auto flex items-center gap-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-time-line" />
                      {item.timeLabel}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-eye-line" />
                      {item.views} 阅读
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-heart-3-line text-primary-500" />
                      {item.likes.toLocaleString()}
                    </span>
                  </div>
                </article>
              </li>
            );
          })}
        </ol>

        <p className="mt-6 text-center text-xs text-foreground-500">
          没有更多了？新进展通常集中在傍晚更新，回来看看有没有你要的下一集。
        </p>
      </div>
    </section>
  );
}